import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Autocomplete from "@mui/material/Autocomplete";
import { toast } from "react-toastify";
import baseUrl from "../../../../api/baseUrl";
import { renderToStaticMarkup } from "react-dom/server";
import ReceiptComponent from "../print/ReciptComponent";
import ReturnRecipt from "../print/ReturnRecipt";
import { getCurrencySymbol } from "../../../../utils/currency/currencyUtils";

export default function SelectedBillPopUp({
  selectedBill,
  openModal,
  handleCloseModal,
}: {
  selectedBill: any;
  openModal: boolean;
  handleCloseModal: () => void;
}) {
  const [edit, setEdit] = useState(false);
  const [payMode, setPayMode] = useState(false);
  const [returnRows, setReturnRows] = useState<any[]>([]);
  const [paymentRows, setPaymentRows] = useState<any[]>([]);
  const [existingReturns, setExistingReturns] = useState<any[]>([]);
  const [creditPayments, setCreditPayments] = useState<any[]>([]);
  const [loadingReturns, setLoadingReturns] = useState(false);

  useEffect(() => {
    if (openModal && selectedBill?.billNumber) {
      setLoadingReturns(true);
      baseUrl
        .get(`/branch/pos/sale-return/${selectedBill.billNumber}`)
        .then((res) => {
          setExistingReturns(res.data?.data || []);
          console.log("Existing returns fetched:", res);
        })
        .catch((err) => {
          console.log("Error fetching existing returns:", err);
          setExistingReturns([]);
        })
        .finally(() => setLoadingReturns(false));

      // Fetch credit payment history for credit sales
      if (selectedBill?.payments?.isCreditSale) {
        baseUrl
          .get(`/branch/sale/credit-payments/${selectedBill.billNumber}`)
          .then((res) => {
            setCreditPayments(res.data?.data || []);
          })
          .catch((err) => {
            console.log("Error fetching credit payments:", err);
            setCreditPayments([]);
          });
      }
    } else {
      setExistingReturns([]);
      setCreditPayments([]);
    }
  }, [openModal, selectedBill]);

  // Reset returnRows when modal opens/closes or edit mode toggles
  useEffect(() => {
    if (!edit) setReturnRows([]);
  }, [edit, openModal]);

  // Reset paymentRows when modal opens/closes or pay mode toggles
  useEffect(() => {
    if (!payMode) setPaymentRows([]);
  }, [payMode, openModal]);

  const handleEditModal = () => {
    setEdit(true);
  };

  const handlePayMode = () => {
    setPayMode(true);
    // Initialize with one payment row
    setPaymentRows([
      {
        cash: 0,
        card: 0,
        bank: 0,
      },
    ]);
  };

  const handleSaveReturn = async () => {
    try {
      if (returnRows.length === 0) {
        toast.error("Please add at least one product to return.");
        return;
      }
      for (const row of returnRows) {
        if (!row.product?.id) {
          toast.error("Please select a product in each row.");
          return;
        }
      }

      const payload = {
        billNumber: selectedBill?.billNumber,
        products: returnRows.map((row) => ({
          itemId: row.product.itemId,
          batchNumberId: row.product.batchNumberId,
          priceHistoryId: row.product.priceHistoryId,
          quantity: Number(row.quantity),
          givenPrice: Number(row.price || 0),
          deduction: Number(row.deduction || 0),
          saleStatus: "Return",
        })),
      };

      baseUrl
        .post("branch/pos/sale-return", payload)
        .then((response) => {
          toast.success("Return saved successfully!");
        })
        .catch((error) => {
          console.error("Error saving return:", error);
          toast.error("Failed to save return. Please try again.");
        });

      setEdit(false);
      setReturnRows([]);
      handleCloseModal();
    } catch (error) {
      console.error("Save failed:", error);
      alert("Failed to save return. Please try again.");
    }
  };

  const handleSavePayment = async () => {
    try {
      if (paymentRows.length === 0) {
        toast.error("Please add at least one payment.");
        return;
      }

      // Calculate total payment amount
      const totalPayment = paymentRows.reduce((sum, row) => {
        return (
          sum +
          Number(row.cash || 0) +
          Number(row.card || 0) +
          Number(row.bank || 0)
        );
      }, 0);

      if (totalPayment <= 0) {
        toast.error("Payment amount must be greater than 0.");
        return;
      }

      const payload = {
        billNumber: selectedBill?.billNumber,
        payments: paymentRows.map((row) => ({
          cash: Number(row.cash || 0),
          card: Number(row.card || 0),
          bank: Number(row.bank || 0),
        })),
      };

      // const response =
      await baseUrl.post("branch/sale/credit-payment", payload);
      toast.success("Payment saved successfully!");

      setPayMode(false);
      setPaymentRows([]);
      handleCloseModal();
    } catch (error) {
      console.error("Payment failed:", error);
      toast.error("Failed to save payment. Please try again.");
    }
  };

  const printReceiptWindow = (content: string, title: string) => {
    const printWindow = window.open("", "_blank", "width=420,height=900");

    if (!printWindow) {
      alert("Unable to open print window. Please allow popups for this site.");
      return;
    }

    printWindow.document.open();
    printWindow.document.write(`
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>${title}</title>
        <style>
          @import url("https://fonts.googleapis.com/css2?family=Stick+No+Bills:wght@400;700&display=swap");
          @page {
            size: 80mm auto;
            margin: 0;
          }
          html, body {
            margin: 0;
            padding: 0;
            background: white;
            width: 100%;
          }
          body {
            display: flex;
            justify-content: center;
            align-items: flex-start;
          }
          .receipt {
            width: 350px;
            margin: 0 auto;
            background-color: white;
            color: black;
            box-sizing: border-box;
          }
          .receipt > * {
            font-family: "Stick No Bills";
            text-transform: uppercase;
            background-color: transparent;
            color: inherit;
          }
          .receipt.mmk-chiba > * {
            font-family: "Arial Narrow", Arial, sans-serif !important;
            font-weight: 1000 !important;
            text-transform: uppercase !important;
          }
          .receipt > div {
            padding: 10px;
          }
          .receipt hr {
            border: 1px dotted;
          }
          .receipt .headers {
            background-color: white;
            color: black;
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          .receipt .logo {
            margin-bottom: 5px;
          }
          .receipt .logo img {
            width: 60px;
            height: 60px;
            object-fit: contain;
          }
          .receipt .title {
            font-size: 50px;
            text-align: center;
            word-wrap: break-word;
            width: 100%;
          }
          .receipt .address,
          .receipt .address-number,
          .receipt .registration-number,
          .receipt .tell,
          .receipt .email,
          .receipt .bilNUmber,
          .receipt .customer-name {
            text-align: center;
            word-wrap: break-word;
            width: 100%;
          }
          .receipt .customer-name { font-weight: bold; }
          .receipt .sale-type {
            font-weight: bold;
            text-align: center;
            margin: 5px 0;
            font-size: 14px;
          }
          .receipt .sale-type.credit {
            color: #4caf50;
            border: 1px dashed #4caf50;
            padding: 2px 0;
          }
          .receipt .sale-type.return {
            color: #ff9800;
            border: 1px dashed #ff9800;
            padding: 2px 0;
          }
          .receipt .time_r {
            background-color: white;
            color: black;
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 4px;
          }
          .receipt .date_r,
          .receipt .name_r {
            background-color: white;
            color: black;
            font-size: 14px;
          }
          .receipt .heading {
            background-color: white;
            color: black;
          }
          .receipt .heading .row1,
          .receipt .r_items .row1 {
            display: flex;
            justify-content: flex-start;
            gap: 20px;
          }
          .receipt .heading .row2,
          .receipt .r_items .row2 {
            display: flex;
            justify-content: space-between;
            gap: 20px;
          }
          .receipt .r_items {
            background-color: white;
            color: black;
          }
          .receipt .amounts {
            display: flex;
            flex-direction: column;
            width: 100%;
            background-color: white;
            color: black;
          }
          .receipt .amounts .datas {
            display: flex;
            justify-content: space-between;
          }
          .receipt .amounts .datas.nettotal-row span {
            font-weight: 900;
            font-size: 1.08em;
          }
          .receipt .thank {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 50px;
            font-size: 20px;
          }
          .receipt .credit-notice {
            margin: 10px 0;
            text-align: center;
            color: #4caf50;
            font-weight: bold;
            border-top: 1px dashed #4caf50;
            border-bottom: 1px dashed #4caf50;
            padding: 5px 0;
          }
          .receipt .credit-notice p {
            margin: 2px 0;
            font-size: 12px;
          }
          @media print {
            body {
              margin: 0 !important;
              background: white !important;
            }
            .receipt {
              width: 80mm !important;
              min-width: 0;
              max-width: 100vw;
              padding: 0 !important;
              margin: 0 auto !important;
              background: white !important;
              box-shadow: none !important;
            }
            .receipt > div {
              padding: 4px !important;
            }
            .receipt .title {
              font-size: 32px !important;
            }
            .receipt .thank {
              font-size: 16px !important;
              height: 32px !important;
            }
          }
        </style>
      </head>
      <body>
        ${content}
        <script>
          window.onload = function () {
            setTimeout(function () {
              window.focus();
              window.print();
            }, 300);
          };
          window.onafterprint = function () {
            window.close();
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handlePrint = () => {
    if (!selectedBill) {
      return;
    }

    const content = renderToStaticMarkup(
      <ReceiptComponent
        selectedBill={selectedBill}
        finalProducts={finalProducts}
        finalPayments={finalPayments}
      />,
    );

    printReceiptWindow(content, "Sales Receipt");
  };

  const handlePrintReturn = () => {
    if (!selectedBill || !existingReturns.length) {
      return;
    }

    const content = renderToStaticMarkup(
      <ReturnRecipt
        selectedBill={selectedBill}
        returnItems={existingReturns}
        payments={payments}
      />,
    );

    printReceiptWindow(content, "Return Receipt");
  };

  if (!selectedBill) return null;

  const { products = [], payments = {} } = selectedBill;
  console.log("Selected Bill:", selectedBill);

  // prefer explicit customer object if present; some responses may include customer under root or under payments
  const customer = selectedBill?.customer ?? payments?.customer ?? null;
  const customerName =
    customer?.name ?? customer?.customerName ?? "Walk-In Customer";
  const customerPhone = customer?.phoneNumber ?? customer?.phone ?? "—";

  // Calculate already returned quantity for each product
  const getReturnedQty = (itemId: number) => {
    return existingReturns
      .filter((r: any) => r.itemId === itemId)
      .reduce((sum: number, r: any) => sum + Number(r.quantity), 0);
  };

  // Compute final products after deducting returned quantities
  const getFinalProducts = () => {
    return (selectedBill?.products || []).map((product: any) => {
      const returnedQty = getReturnedQty(product.itemId);
      return {
        ...product,
        quantity: Math.max(Number(product.quantity) - returnedQty, 0),
      };
    });
  };

  // Compute the total value of returned products
  const getReturnedTotal = () => {
    return existingReturns.reduce((sum: number, ret: any) => {
      const price = Number(ret.givenPrice || ret.price || 0);
      const deduction = Number(ret.deduction || 0);
      const qty = Number(ret.quantity || 0);
      return sum + (price - deduction) * qty;
    }, 0);
  };

  // Compute final payment details after deducting returned products' total
  const getFinalPayments = () => {
    const returnedTotal = getReturnedTotal();
    return {
      ...payments,
      netTotal: Math.max(Number(payments.netTotal) - returnedTotal, 0),
      total: Math.max(Number(payments.total) - returnedTotal, 0),
      amountRecived: Math.max(
        Number(payments.amountRecived) - returnedTotal,
        0,
      ),
      balance: Math.max(Number(payments.balance) - returnedTotal, 0),
      // cash, card, bank remain unchanged
      // unpaidAmount: handled below
    };
  };

  const finalProducts = getFinalProducts();
  const finalPayments = getFinalPayments();

  // Compute totals for summary (based on finalProducts after returns)
  const computeProductsTotals = () => {
    const prods = finalProducts || [];
    let totalQty = 0;
    let gross = 0;
    let totalDiscount = 0;
    let totalTax = 0;

    prods.forEach((p: any) => {
      const qty = Number(p.quantity || 0);
      const price = Number(p.price || 0);
      const lineDiscount = Number(p.lineDiscount || 0);
      const tax = Number(p.tax || 0);

      totalQty += qty;
      gross += price * qty;
      totalDiscount += lineDiscount * qty;
      totalTax += tax * qty;
    });

    const subtotal = gross - totalDiscount;
    return { totalQty, gross, totalDiscount, totalTax, subtotal };
  };

  const totals = computeProductsTotals();
  const returnedTotal = getReturnedTotal();
  const creditDue = Number(
    payments?.unpaidAmount || payments?.creditAmount || 0,
  );
  const netTotal = Number(finalPayments?.netTotal || 0);
  const amountReceived = Number(finalPayments?.amountRecived || 0);
  const balance = Number(finalPayments?.balance || 0);

  const currencySymbol = getCurrencySymbol();

  // Calculate unpaid amount after returns
  const unpaidAmountAfterReturns = Math.max(
    Number(finalPayments?.unpaidAmount ?? creditDue ?? 0),
    0,
  );

  // Handler for adding a new row
  const handleAddRow = () => {
    setReturnRows([
      ...returnRows,
      {
        product: null,
        quantity: 1,
        price: "",
        lineDiscount: "",
        deduction: "",
      },
    ]);
  };

  // Handler for changing a field in a row
  const handleRowChange = (idx: number, field: string, value: any) => {
    const updatedRows = [...returnRows];
    if (field === "product") {
      updatedRows[idx].product = value;
      updatedRows[idx].price = value?.price || "";
      updatedRows[idx].lineDiscount = value?.lineDiscount || "";
      updatedRows[idx].quantity = 1;
    } else {
      updatedRows[idx][field] = value;
    }
    setReturnRows(updatedRows);
  };

  // Handler for removing a row by index
  const handleRemoveRow = (idx: number) => {
    setReturnRows((prevRows) => prevRows.filter((_, i) => i !== idx));
  };

  // Handler for adding a new payment row
  const handleAddPaymentRow = () => {
    setPaymentRows([
      ...paymentRows,
      {
        cash: 0,
        card: 0,
        bank: 0,
      },
    ]);
  };

  // Handler for changing a field in a payment row
  const handlePaymentRowChange = (idx: number, field: string, value: any) => {
    const updatedRows = [...paymentRows];
    updatedRows[idx][field] = value;
    setPaymentRows(updatedRows);
  };

  // Handler for removing a payment row by index
  const handleRemovePaymentRow = (idx: number) => {
    setPaymentRows((prevRows) => prevRows.filter((_, i) => i !== idx));
  };

  // Ensure edit and payMode are reset when modal closes
  const handleClose = () => {
    setEdit(false);
    setPayMode(false);
    setReturnRows([]);
    setPaymentRows([]);
    setCreditPayments([]);
    handleCloseModal();
  };

  // Check if this is a credit sale with unpaid amount
  const isCreditSaleWithUnpaidAmount = selectedBill?.payments?.isCreditSale;

  return (
    <Dialog
      open={openModal}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      className="invoice-detail-dialog"
    >
      <DialogTitle>
        Sell Details (Invoice No.: #{payments.billNumber || "—"})
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className="invoice-popup-content">
        <div className="section">
          <Typography>
            <strong>Date:</strong>{" "}
            {new Date(payments.createdAt).toLocaleString()}
          </Typography>
          <Typography>
            <strong>Status:</strong> Final
          </Typography>
          {/* <Typography>
            <strong>Payment Status:</strong> Paid
          </Typography> */}
          <Typography>
            <strong>Customer Name:</strong> {customerName}
          </Typography>
          <Typography>
            <strong>Phone:</strong> {customerPhone}
          </Typography>
          {/* <Typography>
            <strong>Address:</strong> {customerAddress}
          </Typography> */}
          {/* <Typography>
            <strong>Shipping:</strong> {shippingInfo}
          </Typography> */}
        </div>

        {/* Products Table */}
        <div className="section" style={{ overflowX: "auto" }}>
          <h4>Products:</h4>
          <div style={{ minWidth: 600, maxWidth: "100%", overflowX: "auto" }}>
            <table
              className="employees-table"
              style={{ width: "100%", minWidth: 600 }}
            >
              <thead>
                <tr>
                  <th>#</th>
                  <th>Product</th>
                  <th>Quantity</th>
                  <th>Unit Price</th>
                  <th>Discount</th>
                  <th>Tax</th>
                  <th>Price inc. Tax</th>
                  <th>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product: any, index: number) => {
                  const qty = Number(product.quantity);
                  const price = Number(product.price);
                  const discount = Number(product.lineDiscount || 0);
                  // tax field on product is a percentage — convert to money amount per unit
                  const taxPct = Number(product.tax || 0);
                  const tax = (taxPct / 100) * (price - discount);
                  const priceIncTax = price - discount + tax;
                  const subtotal = priceIncTax * qty;

                  return (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>
                        {product["Item.name"]} ({product["Item.barcode"]})
                      </td>
                      <td>{qty.toFixed(2)} Pc(s)</td>
                      <td>
                        {currencySymbol} {price.toFixed(2)}
                      </td>
                      <td>
                        {currencySymbol} {discount.toFixed(2)}
                      </td>
                      <td>
                        {currencySymbol} {tax.toFixed(2)}
                      </td>
                      <td>
                        {currencySymbol} {priceIncTax.toFixed(2)}
                      </td>
                      <td>
                        {currencySymbol} {subtotal.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary below products */}
        <div
          className="section"
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 8,
            background: "#fafafa",
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <div style={{ flex: "1 1 220px" }}>
            <h4 style={{ margin: 0, marginBottom: 8 }}>Summary</h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 6,
              }}
            >
              <div style={{ color: "#666" }}>Total Quantity</div>
              <div style={{ fontWeight: 700 }}>{totals.totalQty}</div>

              <div style={{ color: "#666" }}>Gross Total</div>
              <div style={{ fontWeight: 700 }}>
                {currencySymbol} {totals.gross.toFixed(2)}
              </div>

              <div style={{ color: "#666" }}>Total Discount</div>
              <div style={{ fontWeight: 700, color: "#d32f2f" }}>
                {currencySymbol}{" "}
                {(totals.totalDiscount + Number(payments.discount)).toFixed(2)}
              </div>

              <div style={{ color: "#666" }}>Returned Total</div>
              <div style={{ fontWeight: 700, color: "#ff9800" }}>
                {currencySymbol} {returnedTotal.toFixed(2)}
              </div>

              <div style={{ color: "#666" }}>Tax Total</div>
              <div style={{ fontWeight: 700 }}>
                {currencySymbol} {Number(payments.tax || 0).toFixed(2)}
              </div>

              <div style={{ color: "#666" }}>Subtotal (Gross - Discount)</div>
              <div style={{ fontWeight: 700 }}>
                {currencySymbol}{" "}
                {(
                  totals.subtotal -
                  (totals.totalDiscount + Number(payments.discount))
                ).toFixed(2)}
              </div>
            </div>
          </div>

          <div style={{ flex: "1 1 220px" }}>
            <h4 style={{ margin: 0, marginBottom: 8, visibility: "hidden" }}>
              spacer
            </h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 6,
              }}
            >
              <div style={{ color: "#666" }}>Net Total</div>
              <div style={{ fontWeight: 700 }}>
                {currencySymbol} {netTotal.toFixed(2)}
              </div>

              <div style={{ color: "#666" }}>Amount Received</div>
              <div style={{ fontWeight: 700, color: "#2e7d32" }}>
                {currencySymbol} {amountReceived.toFixed(2)}
              </div>

              <div style={{ color: "#666" }}>Balance</div>
              <div style={{ fontWeight: 700, color: "#d32f2f" }}>
                {currencySymbol} {balance.toFixed(2)}
              </div>

              <div style={{ color: "#666" }}>Credit Due</div>
              <div
                style={{
                  fontWeight: 700,
                  color: creditDue > 0 ? "#d32f2f" : "#666",
                }}
              >
                {currencySymbol} {creditDue.toFixed(2)}
              </div>

              <div style={{ color: "#666" }}>Total (incl. Returns)</div>
              <div style={{ fontWeight: 700 }}>
                {currencySymbol} {(totals.subtotal - returnedTotal).toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        <div>
          <h4>Returns:</h4>
        </div>
        {/* Existing Returns Table */}
        {loadingReturns ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Loading previous returns...
          </Typography>
        ) : (
          existingReturns.length > 0 && (
            <Table
              size="small"
              sx={{
                borderRadius: 3,
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                background: "#fff",
                boxShadow: "0 2px 8px rgba(60, 180, 120, 0.07)",
                tableLayout: "fixed",
                mb: 3,
              }}
            >
              <TableHead>
                <TableRow sx={{ background: "#e0e0e0" }}>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Unit Price</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Discount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Deduction</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {existingReturns.map((ret, idx) => {
                  const price = Number(ret.givenPrice || ret.price || 0);
                  const discount = Number(ret.lineDiscount || 0);
                  const deduction = Number(ret.deduction || 0);
                  const subtotal =
                    (price - deduction) * Number(ret.quantity || 0);
                  return (
                    <TableRow key={idx}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        {ret["Item.name"] || ret?.Item?.name}
                      </TableCell>
                      <TableCell>{ret.quantity}</TableCell>
                      <TableCell>
                        {currencySymbol} {price.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {currencySymbol} {discount.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {currencySymbol} {deduction.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {currencySymbol} {subtotal.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )
        )}

        {edit && (
          <div
            className="section"
            style={{
              borderRadius: 16,
              boxShadow: "0 4px 24px rgba(60, 180, 120, 0.10)",
              padding: 24,
              background: " #f1f8e9",
              marginBottom: 32,
              marginTop: 12,
              border: "1px solid #b2dfdb",
              width: "100%",
              maxWidth: "100%",
              overflow: "visible",
            }}
          >
            <h4
              style={{
                marginBottom: 18,
                color: "#388e3c",
                fontWeight: 700,
                letterSpacing: 1,
                fontSize: 20,
              }}
            >
              Return Products
            </h4>

            {/* New Return Table */}
            <div
              style={{
                width: "100%",
                maxWidth: "100%",
                overflow: "visible",
              }}
            >
              <Table
                size="small"
                sx={{
                  borderRadius: 3,
                  width: "100%",
                  minWidth: 0,
                  maxWidth: "100%",
                  background: "#fff",
                  boxShadow: "0 2px 8px rgba(60, 180, 120, 0.07)",
                  tableLayout: "fixed",
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      background: " #c8e6c9",
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#256029",
                        fontSize: 15,
                        width: 40,
                      }}
                    >
                      #
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#256029",
                        fontSize: 15,
                        minWidth: 100,
                        maxWidth: 200,
                        width: 180,
                      }}
                    >
                      Product
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#256029",
                        fontSize: 15,
                        width: 80,
                      }}
                    >
                      Quantity
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#256029",
                        fontSize: 15,
                        width: 100,
                      }}
                    >
                      Unit Price
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#256029",
                        fontSize: 15,
                        width: 80,
                      }}
                    >
                      Discount
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#256029",
                        fontSize: 15,
                        width: 100,
                      }}
                    >
                      Deduction
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#256029",
                        fontSize: 15,
                        width: 110,
                      }}
                    >
                      Subtotal
                    </TableCell>
                    <TableCell sx={{ width: 50, color: "red" }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {returnRows.map((row, idx) => {
                    const qty = Number(row.quantity) || 0;
                    const price = Number(row.price) || 0;
                    const discount = Number(row.lineDiscount) || 0;
                    const deduction = Number(row.deduction) || 0;
                    const subtotal = (price - deduction) * qty;

                    // Prevent selecting the same product twice and exclude fully-returned products
                    const selectedProductIds = returnRows
                      .map((r) => r.product?.id)
                      .filter(Boolean);
                    const availableProducts = finalProducts.filter(
                      (p: any) =>
                        p.quantity > 0 &&
                        (!selectedProductIds.includes(p.id) ||
                          row.product?.id === p.id),
                    );

                    // Find the max quantity for this product in the original sale minus already returned
                    const maxQty = row.product
                      ? Math.max(
                          (products.find((p: any) => p.id === row.product.id)
                            ?.quantity || 1) -
                            getReturnedQty(row.product.itemId),
                          0,
                        )
                      : 1;

                    return (
                      <TableRow
                        key={idx}
                        sx={{
                          backgroundColor:
                            idx % 2 === 0 ? "#f1f8e9" : "#ffffff",
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell sx={{ minWidth: 250 }}>
                          <Autocomplete
                            size="small"
                            options={availableProducts}
                            getOptionLabel={(option: any) =>
                              option["Item.name"]
                                ? `${option["Item.name"]} (${option["Item.barcode"]})`
                                : ""
                            }
                            value={row.product}
                            onChange={(_, value) =>
                              handleRowChange(idx, "product", value)
                            }
                            renderInput={(params) => (
                              <TextField {...params} label="Select Product" />
                            )}
                            isOptionEqualToValue={(option, value) =>
                              option.id === value?.id
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={row.quantity}
                            onChange={(e) => {
                              let val = Number(e.target.value);
                              if (val > maxQty) val = maxQty;
                              if (val < 1) val = 1;
                              handleRowChange(idx, "quantity", val);
                            }}
                            inputProps={{ min: 1, max: maxQty }}
                            sx={{ width: 80 }}
                            disabled={!row.product}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Max: {maxQty}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ pl: 1 }}>
                            {currencySymbol} {price.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ pl: 1 }}>
                            {currencySymbol} {discount.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={row.deduction || ""}
                            onChange={(e) =>
                              handleRowChange(idx, "deduction", e.target.value)
                            }
                            sx={{ width: 80 }}
                            disabled={!row.product}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ pl: 1 }}>
                            {currencySymbol} {subtotal.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            sx={{
                              color:
                                "linear-gradient(90deg, #66bb6a 0%, #66bb6a 100%)",
                              "&:hover": {
                                color: "#d32f2f",
                              },
                            }}
                            color="error"
                            onClick={() => handleRemoveRow(idx)}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      sx={{
                        background: "#e8f5e9",
                        textAlign: "center",
                        borderBottom: 0,
                      }}
                    >
                      <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddRow}
                        size="small"
                        variant="contained"
                        sx={{
                          fontWeight: 600,
                          borderRadius: 2,
                          background:
                            "linear-gradient(90deg, #66bb6a 0%, #66bb6a 100%)",
                          color: "#256029",
                          boxShadow: "none",
                          letterSpacing: 1,
                          "&:hover": {
                            background: " #388e3c",
                            color: "#fff",
                          },
                        }}
                        disabled={returnRows.length >= products.length}
                      >
                        Add Row
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {payMode && (
          <div
            className="section"
            style={{
              borderRadius: 16,
              boxShadow: "0 4px 24px rgba(33, 150, 243, 0.10)",
              padding: 24,
              background: "#e3f2fd",
              marginBottom: 32,
              marginTop: 12,
              border: "1px solid #90caf9",
              width: "100%",
              maxWidth: "100%",
              overflow: "visible",
            }}
          >
            <h4
              style={{
                marginBottom: 18,
                color: "#1976d2",
                fontWeight: 700,
                letterSpacing: 1,
                fontSize: 20,
              }}
            >
              Make Payment
            </h4>

            <div style={{ marginBottom: 16 }}>
              <Typography
                variant="body1"
                sx={{ color: "#1976d2", fontWeight: 600 }}
              >
                Current Unpaid Amount (after returns): {currencySymbol}{" "}
                {unpaidAmountAfterReturns.toFixed(2)}
              </Typography>
              {returnedTotal > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Returned products value deducted: {currencySymbol}{" "}
                  {returnedTotal.toFixed(2)}
                </Typography>
              )}
            </div>

            {/* Payment Table */}
            <div
              style={{
                width: "100%",
                maxWidth: "100%",
                overflow: "visible",
              }}
            >
              <Table
                size="small"
                sx={{
                  borderRadius: 3,
                  width: "100%",
                  minWidth: 0,
                  maxWidth: "100%",
                  background: "#fff",
                  boxShadow: "0 2px 8px rgba(33, 150, 243, 0.07)",
                  tableLayout: "fixed",
                }}
              >
                <TableHead>
                  <TableRow sx={{ background: "#bbdefb" }}>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#0d47a1",
                        fontSize: 15,
                        width: 40,
                      }}
                    >
                      #
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#0d47a1",
                        fontSize: 15,
                        width: 100,
                      }}
                    >
                      Cash
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#0d47a1",
                        fontSize: 15,
                        width: 100,
                      }}
                    >
                      Card
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#0d47a1",
                        fontSize: 15,
                        width: 100,
                      }}
                    >
                      Bank
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#0d47a1",
                        fontSize: 15,
                        width: 100,
                      }}
                    >
                      Total
                    </TableCell>
                    <TableCell sx={{ width: 50, color: "red" }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentRows.map((row, idx) => {
                    const cash = Number(row.cash) || 0;
                    const card = Number(row.card) || 0;
                    const bank = Number(row.bank) || 0;
                    const total = cash + card + bank;

                    return (
                      <TableRow
                        key={idx}
                        sx={{
                          backgroundColor:
                            idx % 2 === 0 ? "#e3f2fd" : "#ffffff",
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={row.cash || ""}
                            onChange={(e) =>
                              handlePaymentRowChange(
                                idx,
                                "cash",
                                e.target.value,
                              )
                            }
                            sx={{ width: 90 }}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={row.card || ""}
                            onChange={(e) =>
                              handlePaymentRowChange(
                                idx,
                                "card",
                                e.target.value,
                              )
                            }
                            sx={{ width: 90 }}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={row.bank || ""}
                            onChange={(e) =>
                              handlePaymentRowChange(
                                idx,
                                "bank",
                                e.target.value,
                              )
                            }
                            sx={{ width: 90 }}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ pl: 1, fontWeight: 600 }}
                          >
                            {currencySymbol} {total.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="error"
                            onClick={() => handleRemovePaymentRow(idx)}
                            size="small"
                            disabled={paymentRows.length === 1}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      sx={{
                        background: "#e1f5fe",
                        textAlign: "center",
                        borderBottom: 0,
                      }}
                    >
                      <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddPaymentRow}
                        size="small"
                        variant="contained"
                        sx={{
                          fontWeight: 600,
                          borderRadius: 2,
                          background:
                            "linear-gradient(90deg, #2196f3 0%, #2196f3 100%)",
                          color: "#0d47a1",
                          boxShadow: "none",
                          letterSpacing: 1,
                          "&:hover": {
                            background: "#1976d2",
                            color: "#fff",
                          },
                        }}
                      >
                        Add Payment Row
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      sx={{
                        fontWeight: 700,
                        fontSize: 16,
                        color: "#0d47a1",
                      }}
                    >
                      Grand Total Payment:
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: 16,
                        color: "#0d47a1",
                      }}
                    >
                      {currencySymbol}{" "}
                      {paymentRows
                        .reduce((sum, row) => {
                          return (
                            sum +
                            Number(row.cash || 0) +
                            Number(row.card || 0) +
                            Number(row.bank || 0)
                          );
                        }, 0)
                        .toFixed(2)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Payment Info */}
        <div className="section" style={{ overflowX: "auto" }}>
          <h4>Payment Info:</h4>
          <div style={{ minWidth: 600, maxWidth: "100%", overflowX: "auto" }}>
            <table
              className="employees-table"
              style={{ width: "100%", minWidth: 600 }}
            >
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Reference No</th>
                  <th>Amount</th>
                  <th>Payment Mode</th>
                  <th>Payment Type</th>
                </tr>
              </thead>
              <tbody>
                {/* Original payment */}
                <tr>
                  <td>1</td>
                  <td>{new Date(payments.createdAt).toLocaleDateString()}</td>
                  <td>SP2025/{payments.id?.toString().padStart(4, "0")}</td>
                  <td>
                    {currencySymbol} {Number(payments.amountRecived).toFixed(2)}
                  </td>
                  <td>
                    {Number(payments.cash) > 0 && "Cash "}
                    {Number(payments.card) > 0 && "Card "}
                    {Number(payments.bank) > 0 && "Bank "}
                  </td>
                  <td>Initial Sale</td>
                </tr>

                {/* Credit payments */}
                {creditPayments.map((payment: any, index: number) => (
                  <tr key={`credit-${index}`}>
                    <td>{index + 2}</td>
                    <td>{new Date(payment.createdAt).toLocaleDateString()}</td>
                    <td>CP2025/{payment.id?.toString().padStart(4, "0")}</td>
                    <td>
                      {currencySymbol}{" "}
                      {(
                        Number(payment.cash || 0) +
                        Number(payment.card || 0) +
                        Number(payment.bank || 0)
                      ).toFixed(2)}
                    </td>
                    <td>
                      {Number(payment.cash) > 0 && "Cash "}
                      {Number(payment.card) > 0 && "Card "}
                      {Number(payment.bank) > 0 && "Bank "}
                    </td>
                    <td>Credit Payment</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Credit Payment History - Keep this section for detailed breakdown */}
        {isCreditSaleWithUnpaidAmount && creditPayments.length > 0 && (
          <div className="section" style={{ overflowX: "auto" }}>
            <h4>Credit Payment Breakdown:</h4>
            <div style={{ minWidth: 600, maxWidth: "100%", overflowX: "auto" }}>
              <table
                className="employees-table"
                style={{ width: "100%", minWidth: 600 }}
              >
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Cash</th>
                    <th>Card</th>
                    <th>Bank</th>
                    <th>Total Payment</th>
                    <th>Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {creditPayments.map((payment: any, index: number) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>
                        {new Date(payment.createdAt).toLocaleDateString()}
                      </td>
                      <td>
                        {currencySymbol} {Number(payment.cash || 0).toFixed(2)}
                      </td>
                      <td>
                        {currencySymbol} {Number(payment.card || 0).toFixed(2)}
                      </td>
                      <td>
                        {currencySymbol} {Number(payment.bank || 0).toFixed(2)}
                      </td>
                      <td>
                        {currencySymbol}{" "}
                        {(
                          Number(payment.cash || 0) +
                          Number(payment.card || 0) +
                          Number(payment.bank || 0)
                        ).toFixed(2)}
                      </td>
                      <td>
                        {currencySymbol}{" "}
                        {Number(payment.creditAmount || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        {edit ? (
          <Button
            onClick={handleSaveReturn}
            variant="outlined"
            style={{
              backgroundColor: "#fff",
              color: "#ff9800",
              borderColor: "#ff9800",
            }}
          >
            Save Return
          </Button>
        ) : payMode ? (
          <Button
            onClick={handleSavePayment}
            variant="outlined"
            style={{
              backgroundColor: "#fff",
              color: "#2196f3",
              borderColor: "#2196f3",
            }}
          >
            Save Payment
          </Button>
        ) : (
          <>
            <Button
              onClick={handleEditModal}
              variant="outlined"
              style={{
                backgroundColor: "#fff",
                color: "#4caf50",
                borderColor: "#4caf50",
              }}
            >
              Edit
            </Button>
            {isCreditSaleWithUnpaidAmount && (
              <Button
                onClick={handlePayMode}
                variant="outlined"
                style={{
                  backgroundColor: "#fff",
                  color: "#2196f3",
                  borderColor: "#2196f3",
                }}
              >
                Pay
              </Button>
            )}
          </>
        )}
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
        <Button onClick={handlePrint} variant="outlined">
          Print
        </Button>
        {existingReturns && existingReturns.length > 0 && (
          <Button onClick={handlePrintReturn} variant="outlined">
            Print Return
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
